// src/utils/mediaUtils.ts
import { playSectionAudio } from "./narrationUtils";
import { isTopVisibleSection } from "./mediaUnlock";
import { AudioController } from "@/utils/audioController";
import { NARRATION_SELECTOR as BASE_NARRATION_SELECTOR } from "@/elements/audio";

// Canonical selectors used throughout StoryCore
export const VIDEO_SELECTOR = 'video';
export const NARRATION_SELECTOR = BASE_NARRATION_SELECTOR;
// ------------------------------------------------------------------------


// ----------------------
const __SC_PLAY_ATTEMPTED = new WeakSet<HTMLVideoElement>();

// Global audio state
// ----------------------
export function isMuted(): boolean {
  return AudioController.isMuted();
}
export function isAudioMuted(): boolean {
  // Back-compat alias: some callers still use isAudioMuted
  return AudioController.isMuted();
}
export function setMuted(muted: boolean, source?: string): void {
  AudioController.setMuted(muted, (source || "mediaUtils") as any);
}
export function setAudioAllowed(allowed: boolean): void {
  AudioController.setAudioAllowed(allowed);
}

// ----------------------
// Media control helpers
// ----------------------
export function pauseAllOtherMedia(current: HTMLVideoElement) {
  try {
    // Do not let off-screen/background videos steal focus and pause narration
    const allow = isTopVisibleSection(current.closest('section'));
    if (!allow) return;

    document.querySelectorAll<HTMLVideoElement>(VIDEO_SELECTOR).forEach(v => { if (v !== current) v.pause(); });
    document.querySelectorAll<HTMLAudioElement>(NARRATION_SELECTOR).forEach(a => a.pause());
  } catch {}
}

export function armInteractionListener(video: HTMLVideoElement) {
  const setInteracted = () => { video.removeEventListener('play', setInteracted, true); setAudioAllowed(true); };
  video.addEventListener('play', setInteracted, { capture: true, once: true } as any);
}

export function stopAllMedia(activeSection?: Element) {
  const scope = activeSection as HTMLElement | undefined;
  try {
    document.querySelectorAll<HTMLAudioElement>(NARRATION_SELECTOR).forEach(audio => {
      if (scope && scope.contains(audio)) return; audio.pause();
    });
    document.querySelectorAll<HTMLVideoElement>(VIDEO_SELECTOR).forEach(video => {
      if (scope && scope.contains(video)) return; video.pause();
    });
  } catch (err) { console.warn("Failed to stop media:", err); }
}

export function isReelSection(section: HTMLElement): boolean {
  try {
    if (!section) return false;

    // 1) Section-level hints (class + data attributes)
    if (section.classList.contains("video-reel")) return true;

    const sds = (section as any).dataset || {};
    if (section.hasAttribute("data-reel") || sds.reel === "true" || sds.reel === "1") {
      return true;
    }
    if (
      section.hasAttribute("data-reel-play-once") ||
      sds.reelPlayOnce === "true" ||
      sds.reelPlayOnce === "1"
    ) {
      return true;
    }

    // 2) Video-level hints inside this section
    const video = section.querySelector<HTMLVideoElement>(VIDEO_SELECTOR);
    if (!video) return false;

    const vds = (video as any).dataset || {};
    if (vds.reel === "true" || vds.reel === "1") return true;
    if (vds.role === "reel") return true;

    return false;
  } catch {
    return false;
  }
}

const __sectionAudioLocks = new WeakMap<HTMLElement, number>();
export async function playSectionAudioOnce(section: HTMLElement): Promise<void> {
  const last = __sectionAudioLocks.get(section) || 0;
  const now = Date.now();
  if (now - last < 1500) return;
  __sectionAudioLocks.set(section, now);
  try {
    const narr = section.querySelector<HTMLAudioElement>(NARRATION_SELECTOR);
    if (narr && narr.paused) await playSectionAudio(narr);
  } catch {} finally {
    setTimeout(() => { if (__sectionAudioLocks.get(section) === now) __sectionAudioLocks.delete(section); }, 1500);
  }
}

export function pauseMediaInSection(section: Element) {
  section.querySelectorAll<HTMLAudioElement>(NARRATION_SELECTOR).forEach(a => a.pause());
  section.querySelectorAll<HTMLVideoElement>(VIDEO_SELECTOR).forEach(v => v.pause());
}

export function forceMute(video: HTMLVideoElement) { video.muted = true; video.setAttribute("muted", ""); }
export function forceUnmute(video: HTMLVideoElement) { video.muted = false; video.removeAttribute("muted"); }

/**
 * Update the section's data-muted attribute to mirror the real media state.
 * Does NOT change global state; just reflects the element.
 */
export function reflectVideoMuteState(section: HTMLElement, video: HTMLVideoElement): void {
  try {
    const actuallyMuted = video.muted || video.volume === 0;
    section.setAttribute("data-muted", actuallyMuted ? "1" : "0");
  } catch {}
}

/**
 * Gesture-safe mute toggle for reel videos.
 * - If currently muted, attempts to unmute and `play()` inside the same gesture.
 *   If the browser blocks it, re-mutes and fires `sc:autoplay-with-audio-failed`.
 * - If currently unmuted, mutes immediately.
 *
 * Returns true if the final state matches the user's intent, false if the browser blocked unmute.
 */
export async function userToggleVideoMute(
  section: HTMLElement,
  video: HTMLVideoElement
): Promise<boolean> {
  const currentlyMuted = video.muted || video.volume === 0;

  // Helper to broadcast HUD-friendly events
  const broadcast = (muted: boolean, err?: unknown) => {
    try {
      section.setAttribute("data-muted", muted ? "1" : "0");

      document.dispatchEvent(
        new CustomEvent("sc:mutechange", {
          detail: { muted, section, video }
        })
      );

      if (!muted && err) {
        document.dispatchEvent(
          new CustomEvent("sc:autoplay-with-audio-failed", {
            detail: { section, video, err }
          })
        );
        document.dispatchEvent(
          new CustomEvent("sc:autoplay-muted", {
            detail: { section, video, err }
          })
        );
      }
    } catch {}
  };

  if (currentlyMuted) {
    // User is trying to unmute under a trusted gesture.
    try {
      const ok = await AudioController.requestUnmuteFromUserGesture(video);

      if (ok) {
        try {
          section.dataset.userAllowed = "1";
          AudioController.setAudioAllowed(true);
        } catch {}

        broadcast(false);
        return true;
      }

      // If the controller could not safely unmute with audio, keep it muted.
      AudioController.requestMute(video);
      broadcast(true, new Error("Unmute with audio was blocked"));
      return false;
    } catch (err) {
      AudioController.requestMute(video);
      broadcast(true, err);
      return false;
    }
  } else {
    // User wants to mute.
    try {
      AudioController.requestMute(video);
    } catch {}
    broadcast(true);
    return true;
  }
}

// ----------------------
// Background video audio mixing
// ----------------------

/**
 * Map author tokens (low/med/high/off) or numeric strings to a volume [0..1].
 * Unknown tokens default to a conservative low value.
 */
export function computeDesiredVideoVolume(token?: string | null): number {
  const s = (token ?? "").toString().toLowerCase().trim();
  if (!s) return 0;
  if (s === "off" || s === "mute" || s === "muted" || s === "false" || s === "0") return 0;
  if (s === "low") return 0.12;
  if (s === "med" || s === "medium") return 0.35;
  if (s === "high") return 0.7;
  const n = parseFloat(s);
  if (!Number.isNaN(n)) return Math.max(0, Math.min(1, n));
  return 0.2;
}

/**
 * Apply background audio policy within a section:
 *  - Only the top-visible section may emit audio.
 *  - Honor global mute and user audio-allowed gating.
 *  - For background videos (data-role='background' or data-bg='true'):
 *      * If enabled, unmute and set volume based on data-video-audio (low/med/high/numeric).
 *      * If narration in the same section is actively playing, keep background at a "ducked" level.
 *      * Otherwise, mute + pause.
 */
export function enforceBackgroundAudioMix(section: HTMLElement): void {
  // 🚫 Do not apply background audio mixer to reel sections.
  // Reels manage their own audio policy and must not be muted here.
  if (isReelSection(section)) {
    return;
  }

  try {
    // Gate by visibility and global mute
    const topVisible = isTopVisibleSection(section);
    const globallyMuted = AudioController.isMuted();
    if (!topVisible || globallyMuted) {
      section.querySelectorAll<HTMLVideoElement>(`${VIDEO_SELECTOR}[data-role="background"], ${VIDEO_SELECTOR}[data-bg="true"]`).forEach(v => {
        try { v.muted = true; v.setAttribute("muted",""); v.volume = 0; v.pause(); } catch {}
      });
      return;
    }

    // Within the active section, check narration state
    const narr = section.querySelector<HTMLAudioElement>(NARRATION_SELECTOR);
    const narrationActive = !!(narr && !narr.muted && !narr.paused);

    section.querySelectorAll<HTMLVideoElement>(`${VIDEO_SELECTOR}[data-role="background"], ${VIDEO_SELECTOR}[data-bg="true"]`).forEach(v => {
      try {
        // Skip reels; they handle their own audio policy
        if (v.dataset.reel === "true" || v.dataset.role === "reel") {
          return;
        }

        // Determine desired base volume from author token
        const rawAudioToken = (v.dataset.videoAudio || v.getAttribute("data-video-audio") || "").toString().toLowerCase();
        const baseVol = computeDesiredVideoVolume(rawAudioToken);
        if (baseVol <= 0) {
          v.muted = true; v.setAttribute("muted",""); v.volume = 0; return;
        }

        // If narration is active, duck but keep background audible without overpowering VO.
        let targetVol = baseVol;
        if (narrationActive) {
          targetVol = Math.max(0.18, Math.min(baseVol, 0.35));
        }

        // Unmute + set volume, and ensure playback if observer intended to play
        v.muted = false;
        v.removeAttribute("muted");
        // Make sure volume reflects desired level (not all browsers honor volume when muted)
        try { v.volume = targetVol; } catch {}
        // Ensure a paused background resumes when narration is active so mix is audible.
        if (narrationActive && v.paused) {
          if (!__SC_PLAY_ATTEMPTED.has(v)) {
            __SC_PLAY_ATTEMPTED.add(v);
            AudioController.safePlay(v, "system");
          } else {
            v.play().catch(() => {});
          }
        }

        // If the observer flagged this for autoplay and it's paused, try to play
        const wantsObserve = (v.dataset.autoplay || "").toLowerCase() === "observe";
        if ((wantsObserve || narrationActive) && v.paused) {
          if (!__SC_PLAY_ATTEMPTED.has(v)) {
            __SC_PLAY_ATTEMPTED.add(v);
            AudioController.safePlay(v, "system");
          }
        }
      } catch {}
    });
  } catch {}
}
